import { useState } from 'react'
import { motion } from 'framer-motion'
import { Mail, Phone, MapPin, Github, Linkedin, Twitter, Send, CheckCircle, AlertCircle } from 'lucide-react'

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  })

  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle')

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormStatus('sending')

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      if (!response.ok) {
        throw new Error('Email delivery failed')
      }

      setFormStatus('success')
      setFormData({ name: '', email: '', subject: '', message: '' })
      setTimeout(() => setFormStatus('idle'), 5000)
    } catch {
      setFormStatus('error')
      setTimeout(() => setFormStatus('idle'), 5000)
    }
  }

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: 'ogenbertrand@gmail.com',
      href: 'mailto:ogenbertrand@gmail.com'
    },
    {
      icon: Phone,
      label: 'Phone',
      value: '+237 653 825 160',
      href: 'tel:+237653825160'
    },
    {
      icon: MapPin,
      label: 'Location',
      value: 'Cameroon',
      href: '#'
    }
  ]

  const socialLinks = [
    { icon: Github, label: 'GitHub', href: 'https://github.com/Ogenbertrand' },
    { icon: Linkedin, label: 'LinkedIn', href: 'https://linkedin.com/in/bertrand-ogen-2a9108264' },
    { icon: Twitter, label: 'Twitter', href: 'https://twitter.com/ogenbertrand' }
  ]

  return (
    <section className="section">
      <div className="container-tight">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0, 0, 0.2, 1] }}
          viewport={{ once: true }}
          className="mb-12 max-w-2xl"
        >
          <span className="section-label mb-4">Contact</span>
          <h2 className="text-balance mb-6">Let&apos;s build something together.</h2>
          <p className="text-lg text-zinc-500 dark:text-zinc-400">
            Open to full-time roles, contract work, and interesting collaborations.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0, 0, 0.2, 1] }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div className="space-y-3">
              {contactInfo.map((info) => (
                <a
                  key={info.label}
                  href={info.href}
                  className="group flex items-center gap-4 rounded-xl border border-zinc-200 bg-white p-4 transition-all duration-200 hover:border-cyan-500/30 hover:shadow-lg hover:shadow-cyan-500/5 dark:border-zinc-800 dark:bg-canvas-dark dark:hover:border-cyan-400/30"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-zinc-100 dark:bg-zinc-900">
                    <info.icon className="h-4 w-4 text-cyan-500" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-zinc-400">
                      {info.label}
                    </div>
                    <div className="text-sm font-medium text-zinc-950 transition-colors duration-200 group-hover:text-cyan-500 dark:text-zinc-50">
                      {info.value}
                    </div>
                  </div>
                </a>
              ))}
            </div>

            <div>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-zinc-400">
                Follow Me
              </h3>
              <div className="flex items-center gap-3">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-zinc-200 bg-white text-zinc-500 transition-colors duration-200 hover:border-cyan-500/30 hover:text-zinc-950 dark:border-zinc-800 dark:bg-canvas-dark dark:hover:text-zinc-50"
                    aria-label={social.label}
                  >
                    <social.icon size={17} strokeWidth={1.5} />
                  </a>
                ))}
              </div>
            </div>

            <div className="card">
              <div className="mb-2 flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-500 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-500" />
                </span>
                <span className="text-sm font-semibold text-zinc-950 dark:text-zinc-50">
                  Available for new opportunities
                </span>
              </div>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                Currently open to discussing full-time positions, contract work, and collaborations.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05, ease: [0, 0, 0.2, 1] }}
            viewport={{ once: true }}
            className="card"
          >
            <h3 className="mb-6 text-base font-semibold text-zinc-950 dark:text-zinc-50">
              Send a message
            </h3>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-zinc-400">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-zinc-400">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-zinc-400">
                  Subject *
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="input"
                  placeholder="What is this about?"
                />
              </div>

              <div>
                <label htmlFor="message" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-zinc-400">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={handleInputChange}
                  className="textarea"
                  placeholder="Tell me about your project..."
                />
              </div>

              <button
                type="submit"
                disabled={formStatus === 'sending'}
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'sending' && (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Sending...
                  </>
                )}
                {formStatus === 'idle' && (
                  <>
                    <Send size={15} strokeWidth={1.5} />
                    Send Message
                  </>
                )}
                {formStatus === 'success' && (
                  <>
                    <CheckCircle size={17} strokeWidth={1.5} />
                    Message Sent
                  </>
                )}
                {formStatus === 'error' && (
                  <>
                    <AlertCircle size={17} strokeWidth={1.5} />
                    Failed to Send
                  </>
                )}
              </button>

              {formStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-lg border border-cyan-100 bg-cyan-50 p-3 text-center text-sm text-cyan-700 dark:border-cyan-900 dark:bg-cyan-950/20 dark:text-cyan-400"
                >
                  Thanks for your message. I&apos;ll get back to you soon.
                </motion.div>
              )}

              {formStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-lg border border-red-100 bg-red-50 p-3 text-center text-sm text-red-700 dark:border-red-900 dark:bg-red-950/20 dark:text-red-400"
                >
                  Something went wrong. Please try again or contact me directly.
                </motion.div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default Contact
